import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import { toast } from "react-toastify";
import * as Icon from "react-feather";
import moment from "moment";
import "moment/locale/es";
import { history } from "../../../history";
import ComprasNotificacionService from "../../../service/compras-notificacion.service";
import { getComprasNotificacionSocket } from "../../../utility/comprasNotificacionSocket";
import { solicitarPermisoNotificaciones, mostrarNotificacionNavegador } from "../../../utility/browserNotification";
import { clasificarNotificacion, stripHtml } from "../../../views/pages/solicitudes-compra/utils/notificacionTipo";
import { ComprasStyles } from "../../../views/pages/solicitudes-compra/styles/compras.styles";
import { NotificacionesStyles } from "../../../views/pages/solicitudes-compra/styles/notificaciones.styles";

moment.locale("es");

const comprasNotificacionService = new ComprasNotificacionService();

const arrayOrVacio = (data) => (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);

const ToastNotificacion = ({ etiqueta, texto, onClick }) => (
  <div onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}>
    <strong>{etiqueta} · Compras</strong>
    <p className="mb-0 mt-1" style={{ fontSize: 13 }}>{texto}</p>
  </div>
);

const NavbarNotificaciones = () => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: rawData } = useQuery("comprasNotificacionLast", comprasNotificacionService.last, {
    refetchInterval: 60000,
  });
  const notificaciones = arrayOrVacio(rawData);
  const pendientes = notificaciones.filter((n) => !n.leida).length;

  const marcarLeida = useCallback(
    async (id) => {
      queryClient.setQueryData("comprasNotificacionLast", (prev) => {
        const anteriores = arrayOrVacio(prev);
        return { ...prev, data: anteriores.map((n) => (n.id === id ? { ...n, leida: true } : n)) };
      });
      const result = await comprasNotificacionService.marcarLeida({ id });
      if (result?.error || result?.success === false) {
        toast.error(result?.message || "No se pudo marcar la notificación como leída.");
        queryClient.invalidateQueries("comprasNotificacionLast");
      }
    },
    [queryClient]
  );

  const marcarTodasLeidas = async () => {
    queryClient.setQueryData("comprasNotificacionLast", (prev) => {
      const anteriores = arrayOrVacio(prev);
      return { ...prev, data: anteriores.map((n) => ({ ...n, leida: true })) };
    });
    const result = await comprasNotificacionService.marcarTodasLeidas();
    if (result?.error || result?.success === false) {
      toast.error(result?.message || "No se pudieron marcar las notificaciones como leídas.");
      queryClient.invalidateQueries("comprasNotificacionLast");
    }
  };

  const irADetalle = useCallback(
    (notificacion) => {
      if (!notificacion.leida) marcarLeida(notificacion.id);
      setIsOpen(false);
      if (notificacion.id_ticket) history.push(`/solicitudes-compra/detalle/${notificacion.id_ticket}`);
    },
    [marcarLeida]
  );

  const verTodas = () => {
    setIsOpen(false);
    history.push("/solicitudes-compra/notificaciones");
  };

  // El usuario decide si autoriza las notificaciones nativas del navegador;
  // si nunca respondió se le pregunta una sola vez al entrar.
  useEffect(() => {
    solicitarPermisoNotificaciones();
  }, []);

  // Tiempo real: cada notificación nueva se antepone al listado sin volver a
  // pedir /last (docs/FRONTEND_COMPRAS_NOTIFICACIONES.md §2), y además se
  // avisa al usuario de forma visible: un toast dentro de la app y, si dio
  // permiso, una notificación nativa del navegador/SO — no solo el contador
  // de la campana.
  useEffect(() => {
    const socket = getComprasNotificacionSocket();
    if (!socket) return;

    const onNotificacion = (notificacion) => {
      queryClient.setQueryData("comprasNotificacionLast", (prev) => {
        const anteriores = arrayOrVacio(prev);
        const siguientes = [notificacion, ...anteriores.filter((n) => n.id !== notificacion.id)].slice(0, 10);
        return { ...prev, data: siguientes };
      });

      const { etiqueta } = clasificarNotificacion(notificacion.tipo);
      const texto = stripHtml(notificacion.mensaje);
      const onClick = notificacion.id_ticket ? () => irADetalle(notificacion) : undefined;

      toast.info(<ToastNotificacion etiqueta={etiqueta} texto={texto} onClick={onClick} />, { autoClose: 8000 });

      mostrarNotificacionNavegador(`Compras · ${etiqueta}`, { body: texto, icon: "/favicon.png", onClick });
    };

    socket.on("compras:notificacion", onNotificacion);

    return () => {
      socket.off("compras:notificacion", onNotificacion);
    };
  }, [queryClient, irADetalle]);

  return (
    <React.Fragment>
      <ComprasStyles />
      <NotificacionesStyles />
      <div className="notif-bell-wrapper">
        <Dropdown isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
          <DropdownToggle tag="button" className="notif-bell-btn" aria-label="Notificaciones">
            <Icon.Bell size={20} />
            {pendientes > 0 && (
              <span className="notif-bell-badge">{pendientes > 9 ? "9+" : pendientes}</span>
            )}
          </DropdownToggle>
          <DropdownMenu right className="notif-panel">
            <div className="notif-panel-header">
              <div className="notif-panel-header-top">
                <p className="notif-panel-title">Notificaciones</p>
                {pendientes > 0 && <span className="notif-pending-tag">{pendientes} PENDIENTES</span>}
              </div>
              <span className="notif-active-tag">
                <span className="notif-active-dot" /> Avisos activos
              </span>
            </div>

            <div className="notif-list">
              {notificaciones.length === 0 ? (
                <div className="notif-empty">No tienes notificaciones por ahora.</div>
              ) : (
                notificaciones.map((n) => {
                  const { severidad, IconCmp, etiqueta } = clasificarNotificacion(n.tipo);
                  return (
                    <div
                      key={n.id}
                      className={`notif-item${n.leida ? "" : " unread"}`}
                      onClick={() => irADetalle(n)}
                    >
                      {!n.leida && <span className="notif-item-dot" />}
                      <div className={`notif-item-icon ${severidad}`}>
                        <IconCmp size={16} />
                      </div>
                      <div className="notif-item-body">
                        <div className="notif-item-top">
                          <span className={`notif-item-badge ${severidad}`}>{etiqueta}</span>
                          <span className="notif-item-time">{moment(n.created_at).fromNow()}</span>
                        </div>
                        <p className="notif-item-msg" dangerouslySetInnerHTML={{ __html: n.mensaje }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="notif-panel-footer">
              {pendientes > 0 && (
                <button type="button" className="filter-btn filter-btn-outline" onClick={marcarTodasLeidas}>
                  Marcar todo como visto
                </button>
              )}
              <button type="button" className="filter-btn filter-btn-primary" onClick={verTodas}>
                Ver todas las notificaciones
              </button>
            </div>
          </DropdownMenu>
        </Dropdown>
      </div>
    </React.Fragment>
  );
};

export default NavbarNotificaciones;
